import React, { useEffect, useState } from 'react';
import { Button } from '../components/Button';
import { LeafIcon, AlertTriangleIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, RefreshCcwIcon, BatteryChargingIcon, DumbbellIcon, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ContactSection } from '../components/ContactSection';
import { SuperfoodsFAQSection } from '../components/SuperfoodsFAQSection';
export const SuperfoodsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'chlorella' | 'jecmen'>('chlorella');
  const [showBusinessText, setShowBusinessText] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Aktualizovaná funkce pro přesměrování na Green Ways - opraveno pro mobil
  const redirectToGreenWays = () => {
    setIsRedirecting(true);
    // Detekce mobilního zařízení
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    setTimeout(() => {
      if (isMobile) {
        // Pro mobilní zařízení použijeme window.location.href se zvacím kódem
        window.location.href = 'https://www.gw-int.net/kategorie?code=bodyhack';
        // Neodstraňujeme loading screen - zůstane až do přesměrování
      } else {
        // Pro desktop použijeme window.open se zvacím kódem
        window.open('https://www.gw-int.net/kategorie?code=bodyhack', '_blank');
        // Reset redirecting state pouze pro desktop
        setTimeout(() => {
          setIsRedirecting(false);
        }, 500);
      }
    }, 1500);
  };
  // Nová funkce pro scrollování na kontaktní formulář na stejné stránce
  const scrollToContactForm = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <>
      <section className="section section-gradient-green relative py-20 min-h-screen" id="greens" data-id="element-1994">
        {/* Redirecting Overlay for Green Ways */}
        <AnimatePresence data-id="element-1995">
          {isRedirecting && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center" data-id="element-1996">
              <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1
          }} className="text-center" data-id="element-1997">
                <div className="mb-6" data-id="element-1998">
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full mx-auto" data-id="element-1999" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2000">
                  Přesměrovávání na stránky GW
                </h3>
                <p className="text-gray-400" data-id="element-2001">Počkejte chvíli...</p>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        <div className="container mx-auto px-4 section-content" data-id="element-2002">
          {/* Header Section with Animated Background */}
          <div className="relative overflow-hidden mb-10" data-id="element-2003">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2004"></div>
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2005"></div>
            <motion.h2 initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="text-4xl md:text-6xl font-black mb-4 text-center" data-id="element-2006">
              <span className="text-green-500" data-id="element-2007">SUPERPOTRAVINY</span> –{' '}
              <span className="relative" data-id="element-2008">
                <span className="relative z-10" data-id="element-2009">ZELENÉ PALIVO</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-green-500/10 rounded-full -z-0" data-id="element-2010"></span>
              </span>{' '}
              PRO TVÉ{' '}
              <span className="relative" data-id="element-2011">
                <span className="relative z-10" data-id="element-2012">TĚLO</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-green-500/10 rounded-full -z-0" data-id="element-2013"></span>
              </span>{' '}
              I{' '}
              <span className="relative" data-id="element-2014">
                <span className="relative z-10" data-id="element-2015">MYSL</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-green-500/10 rounded-full -z-0" data-id="element-2016"></span>
              </span>
            </motion.h2>
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 0.5
          }} viewport={{
            once: true
          }} className="w-24 h-1 bg-gradient-to-r from-green-500/50 via-green-500 to-green-500/50 mx-auto rounded-full mb-6" data-id="element-2017"></motion.div>
          </div>

          {/* Alert Notice - Styled as a premium notice */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-gray-900/90 to-gray-800/90 p-2.5 md:p-3.5 rounded-lg border-l-2 border-amber-500 shadow-lg" data-id="element-2018">
            <div className="flex items-start gap-2.5" data-id="element-2019">
              <div className="bg-amber-500/20 p-1.5 md:p-2 rounded-full flex-shrink-0" data-id="element-2020">
                <AlertTriangleIcon className="w-3.5 h-3.5 md:w-4 md:h-4 text-amber-500" data-id="element-2021" />
              </div>
              <div data-id="element-2022">
                <h4 className="font-bold text-amber-400 mb-0.5 text-xs md:text-sm" data-id="element-2023">
                  UPOZORNĚNÍ! Informace k nákupu superpotravin
                </h4>
                <p className="text-gray-300 text-xs" data-id="element-2024">
                  Pokud klikneš v sekci superpotravin na tlačítko „MÁM ZÁJEM",
                  budeš automaticky přesměrován na oficiální stránky společnosti
                  Green Ways. Důvod je jednoduchý – Green Ways zachovává férový
                  a etický způsob sdílení těchto produktů. Proto jejich prodej
                  neprobíhá přímo na mých stránkách, ale výhradně
                  prostřednictvím oficiálních stránek.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Introduction Section with Enhanced Visual Layout */}
          <div className="max-w-6xl mx-auto mb-16" data-id="element-2025">
            <motion.h3 initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} viewport={{
            once: true
          }} className="text-xl md:text-3xl font-bold mb-6 md:mb-8 text-center" data-id="element-2026">
              <div className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-green-500/20 rounded-full mr-3 mb-2" data-id="element-2027">
                <LeafIcon className="w-5 h-5 md:w-6 md:h-6 text-green-500" data-id="element-2028" />
              </div>
              <span className="relative" data-id="element-2029">
                <span data-id="element-2030">
                  Změň svoje <span className="text-green-500" data-id="element-2031">zdraví</span> od
                  základů. Objev sílu{' '}
                  <span className="text-green-500" data-id="element-2032">chlorelly</span> a{' '}
                  <span className="text-green-500" data-id="element-2033">ječmene</span>.
                </span>
                <span className="absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" data-id="element-2034"></span>
              </span>
            </motion.h3>

            <div className="flex flex-col md:flex-row gap-8 items-stretch" data-id="element-2035">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} viewport={{
              once: true
            }} className="w-full md:w-1/2 relative flex items-stretch" data-id="element-2036">
                <div className="rounded-xl overflow-hidden shadow-[0_20px_50px_-12px_rgba(34,197,94,0.5)] relative w-full h-full" data-id="element-2037">
                  <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 to-green-500/10 rounded-xl z-10" data-id="element-2038"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/qXrXYqyBiBV45rWmXjSTFw/mlady-jecmen-green-ways-napoj-15366006533983_1993633d3396bbe13.jpg" alt="Zelený nápoj z mladého ječmene a chlorelly" className="w-full h-full object-cover object-center rounded-xl" style={{
                  minHeight: '500px'
                }} data-id="element-2039" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-xl" data-id="element-2040"></div>
                  <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/60 to-transparent rounded-b-xl" data-id="element-2041"></div>
                </div>
                <div className="absolute -inset-0.5 bg-gradient-to-br from-green-500/30 to-green-900/40 rounded-xl blur-sm -z-10" data-id="element-2042"></div>
              </motion.div>

              <div className="w-full md:w-1/2 flex flex-col" data-id="element-2043">
                <motion.div initial={{
                opacity: 0,
                x: 30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} viewport={{
                once: true
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-6 rounded-xl border-l-4 border-green-500 luxury-card mb-4 transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)]" data-id="element-2044">
                  <p className="text-sm md:text-lg mb-2.5" data-id="element-2045">
                    <span className="font-semibold text-green-400" data-id="element-2046">
                      Superpotraviny ti nepomůžou jen se svaly.
                    </span>{' '}
                    Mění i <span className="text-green-400" data-id="element-2047">hlavu</span>,{' '}
                    <span className="text-green-400" data-id="element-2048">náladu</span>,{' '}
                    <span className="text-green-400" data-id="element-2049">trávení</span>,{' '}
                    <span className="text-green-400" data-id="element-2050">energii</span> a{' '}
                    <span className="text-green-400" data-id="element-2051">vnitřní klid</span>.
                  </p>
                  <p className="text-sm md:text-lg mb-2.5" data-id="element-2052">
                    Nejsou to žádné trendy doplňky, na které si za měsíc nikdo
                    nevzpomene – zelené potraviny pro mě znamenají{' '}
                    <span className="text-green-400" data-id="element-2053">
                      základní stavební kameny
                    </span>{' '}
                    zdravého těla a silné mysli. A ty samé základy mohou pomoci
                    vybudovat i tobě!
                  </p>
                  <p className="text-sm md:text-lg" data-id="element-2054">
                    <span className="font-semibold text-green-400" data-id="element-2055">
                      Chlorella a ječmen od Green Ways změnily nejen můj život –
                      ale i mému tátovi.
                    </span>{' '}
                    Oba jsme díky nim začali vnímat zásadní rozdíly ve trávení,
                    imunitě i mentální pohodě. A to všechno přirozenou, čistě
                    rostlinnou cestou.
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.5
              }} viewport={{
                once: true
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-6 rounded-xl border-l-4 border-green-500 luxury-card mb-4 transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)]" data-id="element-2056">
                  <div data-id="element-2057">
                    <p className="text-sm md:text-lg font-medium mb-1.5" data-id="element-2058">
                      Možná si říkáš –{' '}
                      <span className="text-green-400" data-id="element-2059">
                        proč zrovna Green Ways
                      </span>{' '}
                      chlorella a ječmen?
                    </p>
                    <button onClick={() => {
                    const element = document.getElementById('gw-reason-more');
                    if (element) {
                      element.classList.toggle('hidden');
                      element.classList.toggle('block');
                    }
                    const button = document.getElementById('gw-reason-button');
                    if (button) {
                      if (element?.classList.contains('hidden')) {
                        button.textContent = 'Více...';
                      } else {
                        button.textContent = 'Méně';
                      }
                    }
                  }} id="gw-reason-button" className="text-green-500 hover:text-green-400 transition-colors text-xs md:text-sm font-medium flex items-center" data-id="element-2060">
                      Více...
                      <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-2061" />
                    </button>
                    <div id="gw-reason-more" className="hidden mt-3 overflow-hidden transition-all duration-300 ease-in-out" data-id="element-2062">
                      <p className="text-sm md:text-lg mb-2.5" data-id="element-2063">
                        Vždyť těch značek je dnes plný internet. Jenže právě v
                        tom je ten rozdíl. Většina produktů se pěstuje někde
                        poblíž, bez ohledu na to, jestli tam vůbec mají ideální
                        podmínky. Green Ways naopak investuje obrovské peníze a
                        úsilí do toho, aby chlorella a ječmen rostly tam, kde
                        mají naprosto přirozené, téměř dokonalé podmínky.
                        Pěstují je na opačných koncích světa – každou
                        superpotravinu zvlášť, přesně tam, kde jí to svědčí
                        nejvíc.
                      </p>
                      <p className="text-sm md:text-lg" data-id="element-2064">
                        Díky tomu si můžeš být jistý, že každý gram, který si
                        dáš do těla, je plný života, živin a účinku – ne jen
                        marketingu. Jsou to živé potraviny, které ti můžou
                        opravdu změnit zdraví.
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.6,
                delay: 0.7
              }} viewport={{
                once: true
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-6 rounded-xl border-l-4 border-green-500 luxury-card mb-4 transition-all duration-300 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)]" data-id="element-2065">
                  <div data-id="element-2066">
                    <p className="text-sm md:text-lg font-medium mb-1.5" data-id="element-2067">
                      A proč zrovna nakupovat{' '}
                      <span className="text-green-400" data-id="element-2068">ode mě</span>?
                    </p>
                    <button onClick={() => {
                    const element = document.getElementById('why-me-more');
                    if (element) {
                      element.classList.toggle('hidden');
                      element.classList.toggle('block');
                    }
                    const button = document.getElementById('why-me-button');
                    if (button) {
                      if (element?.classList.contains('hidden')) {
                        button.textContent = 'Více...';
                      } else {
                        button.textContent = 'Méně';
                      }
                    }
                  }} id="why-me-button" className="text-green-500 hover:text-green-400 transition-colors text-xs md:text-sm font-medium flex items-center" data-id="element-2069">
                      Více...
                      <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-2070" />
                    </button>
                    <div id="why-me-more" className="hidden mt-3 overflow-hidden transition-all duration-300 ease-in-out" data-id="element-2071">
                      <p className="text-sm md:text-lg" data-id="element-2072">
                        Protože se vzdělávám přímo od lidí, kteří u Green Ways
                        působí roky a mají za sebou stovky klientů se kterými
                        spolupracovali, jak s užíváním GW, tak s tím, jak
                        podnikat s GW. Také pomáhali uživatelům GW s tím, jak
                        pomoci lidem okolo sebe a zároveň si budovat pasivní
                        příjem (není to povinné podnikat, člověk může pouze
                        užívat). Učím se od těch nejlepších, abych byl schopný
                        předat dál, informace, které mužou člověku ne jen pomoct
                        zdravotně ale i finančně. Nechci ti něco jen prodat a
                        zmizet. Když mi napíšeš, poradím ti s dávkováním a
                        pomůžu s výběrem.
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.8
              }} viewport={{
                once: true
              }} className="relative mt-auto" data-id="element-2073">
                  <Button variant="primary" size="lg" onClick={() => {
                  const element = document.getElementById('greens-products');
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth'
                    });
                  }
                }} className="luxury-button w-full text-sm md:text-base" data-id="element-2074">
                    <span className="relative z-10" data-id="element-2075">CHCI TO VYZKOUŠET</span>
                  </Button>
                  <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse" data-id="element-2076"></div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Bull Section with Enhanced Visual Elements */}
          <div className="flex flex-col md:flex-row gap-12 mb-28" data-id="element-2077">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-2078">
              <h3 className="text-xl md:text-3xl font-extrabold mb-6 md:mb-8 flex items-center" data-id="element-2079">
                Viděl jsi někdy býka jíst{' '}
                <span className="text-green-500 ml-1 relative" data-id="element-2080">
                  maso
                  <span className="absolute bottom-1 left-0 w-full h-1 bg-green-500/30 rounded-full" data-id="element-2081"></span>
                </span>
                ?
              </h3>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-7 rounded-xl border-l-4 border-green-500 luxury-card mb-6 shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)]" data-id="element-2082">
                <p className="text-sm md:text-lg mb-4" data-id="element-2083">
                  Zamyslel ses někdy, proč vlastně jíme maso kvůli bílkovinám –
                  když{' '}
                  <span className="text-green-400" data-id="element-2084">
                    zvířata, která mají obrovskou sílu, svaly a energii
                  </span>
                  , se živí jen rostlinami?
                </p>
                <p className="text-sm md:text-lg mb-4" data-id="element-2085">
                  Příkladem je býk – jedno z{' '}
                  <span className="text-green-400" data-id="element-2086">
                    nejsilnějších zvířat na planetě
                  </span>
                  . Nemusí sníst ani gram masa, a přesto je masivní, svalnatý a
                  plný energie. Podobně gorily, koně nebo sloni – všechna tato
                  silná zvířata staví svou sílu na rostlinné stravě.
                </p>
                <p className="text-sm md:text-lg mb-4" data-id="element-2087">
                  Masožravci mají krátké trávicí ústrojí a ostré čelisti
                  uzpůsobené ke kousání masa. My lidé máme zuby, délku střev i
                  trávení mnohem podobnější býložravcům.
                </p>
                <p className="text-sm md:text-lg mb-4" data-id="element-2088">
                  To neznamená, že musíš přestat jíst maso.
                </p>
                <p className="text-sm md:text-lg" data-id="element-2089">
                  Ale pokud jíš maso,{' '}
                  <span className="text-green-400" data-id="element-2090">
                    zelené superpotraviny ti pomůžou lépe vstřebat živiny z masa
                    i dalších jídel
                  </span>{' '}
                  – což vede k:
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3 md:gap-6 mb-8" data-id="element-2091">
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.1
              }} viewport={{
                once: true
              }} whileHover={{
                y: -5,
                boxShadow: '0 15px 30px -5px rgba(34, 197, 94, 0.3)'
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-3 md:p-5 rounded-lg text-center border-t-2 border-green-500 luxury-card transition-all duration-300" data-id="element-2092">
                  <div className="bg-green-500/20 p-2 md:p-4 rounded-full inline-flex mb-2 md:mb-4 relative" data-id="element-2093">
                    <RefreshCcwIcon className="w-6 h-6 md:w-8 md:h-8 text-green-500" data-id="element-2094" />
                    <div className="absolute inset-0 bg-green-500/10 rounded-full animate-pulse" data-id="element-2095"></div>
                  </div>
                  <p className="text-xs md:text-base font-medium text-green-400" data-id="element-2096">
                    Rychlejší regenerace
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.2
              }} viewport={{
                once: true
              }} whileHover={{
                y: -5,
                boxShadow: '0 15px 30px -5px rgba(34, 197, 94, 0.3)'
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-3 md:p-5 rounded-lg text-center border-t-2 border-green-500 luxury-card transition-all duration-300" data-id="element-2097">
                  <div className="bg-green-500/20 p-2 md:p-4 rounded-full inline-flex mb-2 md:mb-4 relative" data-id="element-2098">
                    <BatteryChargingIcon className="w-6 h-6 md:w-8 md:h-8 text-green-500" data-id="element-2099" />
                  </div>
                  <p className="text-xs md:text-base font-medium text-green-400" data-id="element-2100">
                    Zdroj důležitých látek
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.3
              }} viewport={{
                once: true
              }} whileHover={{
                y: -5,
                boxShadow: '0 15px 30px -5px rgba(34, 197, 94, 0.3)'
              }} className="bg-gradient-to-br from-gray-900 to-gray-800 p-3 md:p-5 rounded-lg text-center border-t-2 border-green-500 luxury-card transition-all duration-300" data-id="element-2101">
                  <div className="bg-green-500/20 p-2 md:p-4 rounded-full inline-flex mb-2 md:mb-4 relative" data-id="element-2102">
                    <DumbbellIcon className="w-6 h-6 md:w-8 md:h-8 text-green-500" data-id="element-2103" />
                    <div className="absolute inset-0 bg-green-500/10 rounded-full animate-pulse" data-id="element-2104"></div>
                  </div>
                  <p className="text-xs md:text-base font-medium text-green-400" data-id="element-2105">
                    Rychlejší výsledky
                  </p>
                </motion.div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-7 rounded-xl border-l-4 border-green-500 luxury-card mb-8 shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)]" data-id="element-2106">
                <p className="text-sm md:text-lg" data-id="element-2107">
                  A díky tomu, že obsahují až{' '}
                  <span className="text-green-400 font-bold" data-id="element-2108">
                    61 % bílkovin
                  </span>
                  , přirozeně přispívají i k růstu svalové hmoty a celkové
                  regeneraci – a to čistě přírodní cestou.
                </p>
              </div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} viewport={{
              once: true
            }} className="relative" data-id="element-2109">
                <div className="flex justify-center" data-id="element-2110">
                  <Button variant="primary" size="lg" onClick={() => {
                  const element = document.getElementById('greens-products');
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth'
                    });
                  }
                }} className="luxury-button px-8 md:px-10 text-sm md:text-base" data-id="element-2111">
                    CHCI TO VYZKOUŠET
                  </Button>
                </div>
                <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse pointer-events-none" data-id="element-2112"></div>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-2113">
              <div className="h-full flex items-center justify-center" data-id="element-2114">
                <div className="relative w-full h-full" data-id="element-2115">
                  <img src="https://cdn.magicpatterns.com/uploads/ub9aDvB7QEfDNGzzUYy5Ei/ChatGPT_Image_Aug_272C_20252C_07_44_58_PM.png" alt="Býk jíst trávu" className="rounded-xl shadow-2xl w-full h-full object-cover object-[60%_center] md:object-[65%_center]" data-id="element-2116" />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-transparent rounded-xl" data-id="element-2117"></div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Combination Section with Enhanced Design */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto mb-28 relative" data-id="element-2118">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2119"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2120"></div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 md:p-10 luxury-card border border-green-500/30 shadow-[0_10px_40px_-5px_rgba(34,197,94,0.2)] relative overflow-hidden" data-id="element-2121">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2122"></div>

              <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center relative" data-id="element-2123">
                <span className="relative inline-block" data-id="element-2124">
                  <span className="relative z-10" data-id="element-2125">
                    KOMBINACE, KTERÁ FUNGUJE
                  </span>
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2126"></span>
                </span>
              </h3>

              <div className="flex items-center justify-center mb-6 md:mb-8" data-id="element-2127">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-green-500/20 rounded-full flex items-center justify-center mr-5" data-id="element-2128">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vwb5egz7newgdqpuo6r6qu/4a086d28-6024-43ad-b7cb-63b8ce93da6c/Screenshot_2025-07-22_at_18.05.35.png" alt="Chlorella" className="w-10 h-10 md:w-12 md:h-12 object-contain" data-id="element-2129" />
                </div>
                <div className="text-2xl md:text-3xl font-bold text-green-500" data-id="element-2130">
                  +
                </div>
                <div className="w-16 h-16 md:w-20 md:h-20 bg-green-500/20 rounded-full flex items-center justify-center ml-5" data-id="element-2131">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vwb5egz7newgdqpuo6r6qu/4a086d28-6024-43ad-b7cb-63b8ce93da6c/Screenshot_2025-07-22_at_18.05.55.png" alt="Ječmen" className="w-10 h-10 md:w-12 md:h-12 object-contain" data-id="element-2132" />
                </div>
              </div>

              <p className="text-sm md:text-lg mb-4 md:mb-6 text-center" data-id="element-2133">
                Když se <span className="text-green-400" data-id="element-2134">chlorella</span> a{' '}
                <span className="text-green-400" data-id="element-2135">ječmen</span> užívají společně,
                jejich účinky se navzájem{' '}
                <span className="text-green-400" data-id="element-2136">posilují</span>.
              </p>

              <div className="bg-black/30 p-4 md:p-6 rounded-lg mb-6 md:mb-8 border border-green-500/20" data-id="element-2137">
                <p className="text-sm md:text-lg mb-4 md:mb-6 text-center" data-id="element-2138">
                  Výsledek?{' '}
                  <span className="text-green-400" data-id="element-2139">
                    Rychlejší detoxikace těla
                  </span>
                  , <span className="text-green-400" data-id="element-2140">víc energie</span>,{' '}
                  <span className="text-green-400" data-id="element-2141">lepší regenerace</span>,{' '}
                  <span className="text-green-400" data-id="element-2142">silnější imunita</span>,{' '}
                  <span className="text-green-400" data-id="element-2143">zdravější trávení</span> a{' '}
                  <span className="text-green-400" data-id="element-2144">vyrovnaná mysl</span>.
                </p>
                <p className="text-sm md:text-lg mb-0 text-center" data-id="element-2145">
                  Přesně to, co potřebuješ, když chceš být{' '}
                  <span className="text-green-400" data-id="element-2146">silný</span>,{' '}
                  <span className="text-green-400" data-id="element-2147">zdravý</span> a{' '}
                  <span className="text-green-400" data-id="element-2148">v klidu</span>. Když to navíc
                  spojíš s mým tréninkovým plánem, správným užíváním doplňků a
                  disciplínou – transformace těla je nevyhnutelná!
                </p>
              </div>

              <div className="text-center relative" data-id="element-2149">
                <div className="flex justify-center" data-id="element-2150">
                  <Button variant="primary" size="lg" onClick={redirectToGreenWays} className="luxury-button px-8 md:px-10 text-sm md:text-base" data-id="element-2151">
                    OBJEDNAT TEĎ
                  </Button>
                </div>
                <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse pointer-events-none" data-id="element-2152"></div>
              </div>
            </div>
          </motion.div>

          {/* Products Section with Enhanced Cards */}
          <div id="greens-products" className="max-w-5xl mx-auto mb-20" data-id="element-2153">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="mb-10 relative" data-id="element-2154">
              <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2155"></div>

              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-center" data-id="element-2156">
                <span className="relative inline-block" data-id="element-2157">
                  <span className="relative z-10" data-id="element-2158">
                    MOŽNOSTI NÁKUPU SUPERPOTRAVIN
                  </span>
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2159"></span>
                </span>
              </h3>

              <div className="w-24 h-1 bg-gradient-to-r from-green-500/50 via-green-500 to-green-500/50 mx-auto rounded-full mb-8" data-id="element-2160"></div>
            </motion.div>

            {/* One-time Purchase Section */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} viewport={{
            once: true
          }} className="mb-10 relative" data-id="element-2161">
              <div className="absolute -right-10 top-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2162"></div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden luxury-card relative border border-green-500/30" data-id="element-2163">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2164"></div>

                <div className="p-1 bg-green-500 text-white font-bold text-center text-xs" data-id="element-2165">
                  MOŽNOST 1
                </div>

                <div className="p-4 md:p-6" data-id="element-2166">
                  <h4 className="text-lg md:text-xl font-bold mb-3 text-center flex items-center justify-center" data-id="element-2167">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mr-2" data-id="element-2168">
                      <span className="text-green-500 font-bold text-xs" data-id="element-2169">
                        1
                      </span>
                    </div>
                    JEDNORÁZOVÝ NÁKUP
                  </h4>

                  <p className="text-xs md:text-sm mb-3 text-center" data-id="element-2170">
                    Chceš si to nejdřív jen{' '}
                    <span className="text-green-400" data-id="element-2171">zkusit</span>? Můžeš.
                    Objednej si 1 balení chlorelly a/nebo ječmene a sleduj, jak
                    se budeš cítit.
                  </p>

                  <div className="bg-black/30 p-3 md:p-4 rounded-lg mb-4 border border-green-500/20" data-id="element-2172">
                    <p className="text-xs mb-2" data-id="element-2173">
                      Ale buď <span className="text-green-400" data-id="element-2174">trpělivý</span> –
                      reálné výsledky nepřijdou za pár dní, tělo potřebuje čas.
                      Tohle není zázrak. Je to{' '}
                      <span className="text-green-400" data-id="element-2175">přírodní síla</span>,
                      která pracuje každý den – a čím déle ji užíváš, tím víc tě
                      mění. Proto doporučuji hodnotit účinky superpotravin až po
                      delší době pravidelného užívání, kdy se mohou projevit
                      skutečné výsledky.
                    </p>
                    <p className="text-xs mb-0" data-id="element-2176">
                      Jedna chlorela a jeden ječmen, vydrží při pravidelném
                      užívání cca{' '}
                      <span className="text-green-400" data-id="element-2177">
                        měsíc na jednu osobu
                      </span>
                      . Samozřejmě dávky se liší podle váhy člověka a aktuálního
                      potřeby a cíle.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:flex md:flex-row gap-3 justify-center" data-id="element-2178">
                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4
                  }} viewport={{
                    once: true
                  }} whileHover={{
                    y: -5,
                    boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.4)'
                  }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 flex-1 max-w-sm border border-green-500/20 transition-all duration-500" data-id="element-2179">
                      <div className="flex justify-center mb-2 relative" data-id="element-2180">
                        <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" data-id="element-2181"></div>
                        <div className="bg-green-500/10 p-2 rounded-full relative" data-id="element-2182">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vwb5egz7newgdqpuo6r6qu/4a086d28-6024-43ad-b7cb-63b8ce93da6c/Screenshot_2025-07-22_at_18.05.35.png" alt="Chlorella" className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg" data-id="element-2183" />
                        </div>
                      </div>
                      <h5 className="text-sm md:text-base font-bold mb-1 text-center" data-id="element-2184">
                        Chlorella
                      </h5>
                      <p className="text-center mb-1 text-green-400 text-xs" data-id="element-2185">
                        Detoxikace a imunita
                      </p>
                      <p className="text-center mb-2 text-lg font-bold" data-id="element-2186">
                        1200 Kč
                      </p>
                      <Button variant="primary" fullWidth onClick={redirectToGreenWays} className="luxury-button text-xs" data-id="element-2187">
                        MÁM ZÁJEM!
                      </Button>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4,
                    delay: 0.2
                  }} viewport={{
                    once: true
                  }} whileHover={{
                    y: -5,
                    boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.4)'
                  }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 flex-1 max-w-sm border border-green-500/20 transition-all duration-500" data-id="element-2188">
                      <div className="flex justify-center mb-2 relative" data-id="element-2189">
                        <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" data-id="element-2190"></div>
                        <div className="bg-green-500/10 p-2 rounded-full relative" data-id="element-2191">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vwb5egz7newgdqpuo6r6qu/4a086d28-6024-43ad-b7cb-63b8ce93da6c/Screenshot_2025-07-22_at_18.05.55.png" alt="Ječmen" className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg" data-id="element-2192" />
                        </div>
                      </div>
                      <h5 className="text-sm md:text-base font-bold mb-1 text-center" data-id="element-2193">
                        Ječmen
                      </h5>
                      <p className="text-center mb-1 text-green-400 text-xs" data-id="element-2194">
                        Energie a regenerace
                      </p>
                      <p className="text-center mb-2 text-lg font-bold" data-id="element-2195">
                        1200 Kč
                      </p>
                      <Button variant="primary" fullWidth onClick={redirectToGreenWays} className="luxury-button text-xs" data-id="element-2196">
                        MÁM ZÁJEM!
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Long-term Purchase Section */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} className="mb-10 relative" data-id="element-2197">
              <div className="absolute -left-10 top-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2198"></div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden luxury-card relative border border-green-500/30" data-id="element-2199">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2200"></div>

                <div className="p-1 bg-green-500 text-white font-bold text-center text-xs" data-id="element-2201">
                  MOŽNOST 2
                </div>

                <div className="p-4 md:p-6" data-id="element-2202">
                  <h4 className="text-base md:text-lg font-bold mb-3 text-center flex items-center justify-center" data-id="element-2203">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mr-2" data-id="element-2204">
                      <span className="text-green-500 font-bold text-xs" data-id="element-2205">
                        2
                      </span>
                    </div>
                    DLOUHODOBÉ UŽÍVÁNÍ : KUP SI VÍCE BALÍČKŮ NARÁZ, BEZ ZÁVAZKU
                  </h4>

                  <p className="text-xs md:text-sm mb-3 text-center" data-id="element-2206">
                    Chceš jít do toho{' '}
                    <span className="text-green-400" data-id="element-2207">naplno</span> a cítit{' '}
                    <span className="text-green-400" data-id="element-2208">skutečné změny</span>? Pak
                    je klíčové užívat chlorellu a ječmen dlouhodobě a
                    pravidelně. Při pravidelném a dlouhodobém užívání se
                    postupně projeví účinky, jako například:
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4" data-id="element-2209">
                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.1
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-2210">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-2211" />
                      <span className="text-green-400 text-[10px]" data-id="element-2212">
                        Více energie během dne
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.2
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-2213">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-2214" />
                      <span className="text-green-400 text-[10px]" data-id="element-2215">
                        Rychlejší regenerace po tréninku
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.3
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-2216">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-2217" />
                      <span className="text-green-400 text-[10px]" data-id="element-2218">
                        Silnější imunita a zdravé trávení
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.4
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-2219">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-2220" />
                      <span className="text-green-400 text-[10px]" data-id="element-2221">
                        Lepší spánek a psychická pohoda
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.5
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-2222">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-2223" />
                      <span className="text-green-400 text-[10px]" data-id="element-2224">
                        Podpora růstu svalů
                      </span>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.6
                  }} viewport={{
                    once: true
                  }} className="bg-black/30 p-2 rounded-lg border border-green-500/20 flex items-start" data-id="element-2225">
                      <CheckIcon className="w-3 h-3 text-green-500 mr-1 mt-0.5 flex-shrink-0" data-id="element-2226" />
                      <span className="text-green-400 text-[10px]" data-id="element-2227">
                        Mentální výkon a čistá hlava
                      </span>
                    </motion.div>
                  </div>

                  <div className="bg-black/30 p-3 rounded-lg mb-4 border border-green-500/20" data-id="element-2228">
                    <p className="text-xs mb-2 text-center" data-id="element-2229">
                      Tahle možnost je pro lidi, kteří si chcí koupit GW{' '}
                      <span className="text-green-400" data-id="element-2230">
                        jednorázově větší množství
                      </span>{' '}
                      na delší dobu{' '}
                      <span className="text-green-400" data-id="element-2231">
                        bez žádného závazku
                      </span>
                      .
                    </p>
                    <p className="text-xs mb-0 text-center font-semibold" data-id="element-2232">
                      <span className="text-green-400" data-id="element-2233">
                        Pokud nakoupíte 3 produkty Superpotravin, získáte 1
                        zdarma!
                      </span>{' '}
                      Stačí se zaregistrovat při dokončení objednávky!
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:flex md:flex-row gap-3 justify-center" data-id="element-2234">
                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4
                  }} viewport={{
                    once: true
                  }} whileHover={{
                    y: -5,
                    boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.4)'
                  }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 flex-1 max-w-sm border border-green-500/20 transition-all duration-500" data-id="element-2235">
                      <div className="flex justify-center mb-2 relative" data-id="element-2236">
                        <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" data-id="element-2237"></div>
                        <div className="bg-green-500/10 p-2 rounded-full relative" data-id="element-2238">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vwb5egz7newgdqpuo6r6qu/4a086d28-6024-43ad-b7cb-63b8ce93da6c/Screenshot_2025-07-22_at_18.05.35.png" alt="Chlorella" className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg" data-id="element-2239" />
                        </div>
                      </div>
                      <h5 className="text-sm md:text-base font-bold mb-1 text-center" data-id="element-2240">
                        Chlorella
                      </h5>
                      <p className="text-center mb-1 text-green-400 text-xs" data-id="element-2241">
                        Detoxikace a imunita
                      </p>
                      <p className="text-center mb-2 text-lg font-bold" data-id="element-2242">
                        1200 Kč
                      </p>
                      <Button variant="primary" fullWidth onClick={redirectToGreenWays} className="luxury-button text-xs" data-id="element-2243">
                        MÁM ZÁJEM!
                      </Button>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4,
                    delay: 0.2
                  }} viewport={{
                    once: true
                  }} whileHover={{
                    y: -5,
                    boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.4)'
                  }} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-3 flex-1 max-w-sm border border-green-500/20 transition-all duration-500" data-id="element-2244">
                      <div className="flex justify-center mb-2 relative" data-id="element-2245">
                        <div className="absolute inset-0 bg-green-500/10 rounded-full blur-xl" data-id="element-2246"></div>
                        <div className="bg-green-500/10 p-2 rounded-full relative" data-id="element-2247">
                          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/vwb5egz7newgdqpuo6r6qu/4a086d28-6024-43ad-b7cb-63b8ce93da6c/Screenshot_2025-07-22_at_18.05.55.png" alt="Ječmen" className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg" data-id="element-2248" />
                        </div>
                      </div>
                      <h5 className="text-sm md:text-base font-bold mb-1 text-center" data-id="element-2249">
                        Ječmen
                      </h5>
                      <p className="text-center mb-1 text-green-400 text-xs" data-id="element-2250">
                        Energie a regenerace
                      </p>
                      <p className="text-center mb-2 text-lg font-bold" data-id="element-2251">
                        1200 Kč
                      </p>
                      <Button variant="primary" fullWidth onClick={redirectToGreenWays} className="luxury-button text-xs" data-id="element-2252">
                        MÁM ZÁJEM!
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Autoship Section */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} viewport={{
            once: true
          }} className="mb-10 relative" data-id="element-2253">
              <div className="absolute -right-10 bottom-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2254"></div>

              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl overflow-hidden luxury-card relative border border-green-500/30" data-id="element-2255">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2256"></div>

                <div className="p-1 bg-green-500 text-white font-bold text-center text-xs" data-id="element-2257">
                  MOŽNOST 3
                </div>

                <div className="absolute top-0 right-0 mt-[2px] mr-[-5px] bg-gradient-to-r from-green-600 to-green-500 text-white text-[10px] md:text-xs font-bold px-3 py-0.5 rounded-full shadow-lg border-2 border-white z-10 transform rotate-2 animate-pulse" data-id="element-2258">
                  NEJPRODÁVANĚJŠÍ
                </div>

                <div className="p-4 md:p-6" data-id="element-2259">
                  {/* Nadpis a základní popis */}
                  <h4 className="text-lg md:text-xl font-bold mb-3 text-center" data-id="element-2260">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-2261">
                      <span className="text-green-500 font-bold text-xs" data-id="element-2262">
                        3
                      </span>
                    </div>
                    CHCI, ABY MI SUPERPOTRAVINY CHODILY KAŽDÝ MĚSÍC (AUTOSHIP)
                  </h4>

                  <p className="text-xs md:text-sm mb-4 text-center" data-id="element-2263">
                    Tahle možnost je pro tebe, pokud chceš mít svůj{' '}
                    <span className="text-green-400" data-id="element-2264">
                      pravidelný přísun zdraví
                    </span>{' '}
                    a zároveň{' '}
                    <span className="text-green-400" data-id="element-2265">pomáhat svému okolí</span>.
                  </p>

                  {/* Co dostaneš */}
                  <div className="bg-black/30 p-3 rounded-lg mb-4 border border-green-500/20" data-id="element-2266">
                    <p className="text-center mb-1 text-sm font-medium text-white" data-id="element-2267">
                      Každý měsíc ti dorazí:
                    </p>
                    <p className="text-center text-xl font-bold text-green-400" data-id="element-2268">
                      2× Chlorella + 2× Ječmen
                    </p>
                  </div>

                  {/* Proč tolik */}
                  <div className="mb-4" data-id="element-2269">
                    <div className="flex items-center mb-2" data-id="element-2270">
                      <div className="w-1 h-8 bg-green-500 rounded-full mr-3" data-id="element-2271"></div>
                      <h5 className="text-sm font-medium text-green-400" data-id="element-2272">
                        Proč tolik?
                      </h5>
                    </div>

                    <div className="flex flex-col md:flex-row gap-3 mb-3" data-id="element-2273">
                      <div className="flex-1 bg-black/20 p-3 rounded-lg border border-green-500/10" data-id="element-2274">
                        <p className="text-xs text-center" data-id="element-2275">
                          <span className="text-green-400 font-bold" data-id="element-2276">
                            1× Chlorella
                          </span>{' '}
                          +{' '}
                          <span className="text-green-400 font-bold" data-id="element-2277">
                            1× Ječmen
                          </span>
                          <br data-id="element-2278" />
                          pro tebe
                        </p>
                      </div>

                      <div className="flex items-center justify-center" data-id="element-2279">
                        <span className="text-green-500" data-id="element-2280">+</span>
                      </div>

                      <div className="flex-1 bg-black/20 p-3 rounded-lg border border-green-500/10" data-id="element-2281">
                        <p className="text-xs text-center" data-id="element-2282">
                          <span className="text-green-400 font-bold" data-id="element-2283">
                            1× Chlorella
                          </span>{' '}
                          +{' '}
                          <span className="text-green-400 font-bold" data-id="element-2284">
                            1× Ječmen
                          </span>
                          <br data-id="element-2285" />
                          pro rodinu/kamarády
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Co je nejlepší */}
                  <div className="mb-4" data-id="element-2286">
                    <div className="flex items-center mb-2" data-id="element-2287">
                      <div className="w-1 h-8 bg-green-500 rounded-full mr-3" data-id="element-2288"></div>
                      <h5 className="text-sm font-medium text-green-400" data-id="element-2289">
                        A co je na tom nejlepší?
                      </h5>
                    </div>

                    <div className="bg-black/20 p-3 rounded-lg border border-green-500/10" data-id="element-2290">
                      <p className="text-xs mb-0" data-id="element-2291">
                        Díky autoshipu dostáváš{' '}
                        <span className="text-green-400 font-bold" data-id="element-2292">
                          1 chlorellu nebo 1 ječmen ZDARMA každý měsíc
                        </span>
                        . To znamená:{' '}
                        <span className="text-green-400" data-id="element-2293">pomáháš sobě</span> i{' '}
                        <span className="text-green-400" data-id="element-2294">druhým</span>, ale
                        zároveň máš{' '}
                        <span className="text-green-400" data-id="element-2295">
                          jeden balíček měsíčně zdarma
                        </span>
                        .
                      </p>
                    </div>
                  </div>

                  {/* Cena */}
                  <div className="bg-gradient-to-br from-green-900/30 to-gray-900 p-4 rounded-xl mb-5 border border-green-500/30 shadow-[0_10px_25px_-5px_rgba(34,197,94,0.2)]" data-id="element-2296">
                    <h5 className="text-base font-bold mb-2 text-center" data-id="element-2297">
                      CENA AUTOSHIPU NA JEDEN MĚSÍC
                    </h5>

                    <p className="text-2xl font-bold text-center text-green-400 mb-3" data-id="element-2298">
                      3600 Kč
                    </p>

                    <div className="mb-2" data-id="element-2299">
                      <p className="text-xs text-center font-bold text-white" data-id="element-2300">
                        Proč je to{' '}
                        <span className="text-green-400" data-id="element-2301">výhodné</span>?
                      </p>
                    </div>

                    <div className="bg-black/30 p-3 rounded-lg" data-id="element-2302">
                      <p className="text-xs mb-3 text-center" data-id="element-2303">
                        Tohle je cena, kterou by si normálně{' '}
                        <span className="text-green-400" data-id="element-2304">
                          zaplatil za 3 balíčky
                        </span>{' '}
                        (například 2× chlorella, 1× ječmen). Jediný rozdíl v
                        autoshipu od normálního nákupu je, že ti objednávka bude
                        chodit,{' '}
                        <span className="text-green-400" data-id="element-2305">
                          aniž by si ji musel zadávat, včetně slevy každý měsíc!
                        </span>
                      </p>

                      <div className="flex justify-center" data-id="element-2306">
                        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-3 rounded-lg border border-green-500/30 shadow-[0_5px_15px_-3px_rgba(34,197,94,0.3)] inline-block mx-auto" data-id="element-2307">
                          <p className="text-center block font-bold text-sm md:text-base" data-id="element-2308">
                            <span className="text-green-400" data-id="element-2309">
                              HODNOTA ZDARMA : 1200 Kč
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tlačítka */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-2310">
                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4
                  }} viewport={{
                    once: true
                  }} className="relative" data-id="element-2311">
                      <Button variant="primary" size="md" onClick={redirectToGreenWays} className="luxury-button px-6 py-3 text-sm w-full sm:w-auto" data-id="element-2312">
                        MÁM ZÁJEM!
                      </Button>
                      <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse" data-id="element-2313"></div>
                    </motion.div>

                    <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.4,
                    delay: 0.2
                  }} viewport={{
                    once: true
                  }} className="relative" data-id="element-2314">
                      <Button variant="outline" size="md" onClick={scrollToContactForm} className="shine py-3 px-6 text-sm w-full sm:w-auto" data-id="element-2315">
                        CHCI PODNIKAT S GW
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Business Section */}
            <div id="gw-business" className="max-w-4xl mx-auto" data-id="element-2316">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} viewport={{
              once: true
            }} className="relative" data-id="element-2317">
                <div className="absolute -left-10 top-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2318"></div>
                <div className="absolute -right-10 bottom-10 w-40 h-40 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2319"></div>

                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 md:p-10 luxury-card border border-green-500/30 shadow-[0_10px_25px_-5px_rgba(34,197,94,0.2)] relative overflow-hidden" data-id="element-2320">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2321"></div>
                  <div className="absolute -right-20 -top-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2322"></div>
                  <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2323"></div>

                  <h4 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-center relative" data-id="element-2324">
                    <span className="relative inline-block" data-id="element-2325">
                      <span className="relative z-10" data-id="element-2326">
                        CHCEŠ PODNIKAT S{' '}
                        <span className="text-green-500" data-id="element-2327">GREEN WAYS</span>?
                      </span>
                      <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2328"></span>
                    </span>
                  </h4>

                  <div className="text-center mb-4 md:mb-6" data-id="element-2329">
                    <button onClick={() => setShowBusinessText(!showBusinessText)} className="text-green-500 hover:text-green-400 transition-colors text-sm md:text-lg font-medium flex items-center mx-auto bg-black/30 px-4 md:px-5 py-3 md:py-2 rounded-full border border-green-500/30 hover:border-green-500/50 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)] transition-all duration-300 relative z-20 touch-manipulation" style={{
                    WebkitTapHighlightColor: 'transparent'
                  }} data-id="element-2330">
                      {showBusinessText ? <>
                          zobrazit méně
                          <ChevronUpIcon className="w-4 h-4 md:w-5 md:h-5 ml-1" data-id="element-2331" />
                        </> : <>
                          chci vědět více...
                          <ChevronDownIcon className="w-4 h-4 md:w-5 md:h-5 ml-1" data-id="element-2332" />
                        </>}
                    </button>
                  </div>

                  <AnimatePresence data-id="element-2333">
                    {showBusinessText && <motion.div initial={{
                    opacity: 0,
                    maxHeight: 0
                  }} animate={{
                    opacity: 1,
                    maxHeight: 2000
                  }} exit={{
                    opacity: 0,
                    maxHeight: 0
                  }} transition={{
                    duration: 0.6,
                    ease: [0.4, 0, 0.2, 1]
                  }} style={{
                    willChange: 'max-height, opacity',
                    transform: 'translateZ(0)'
                  }} className="overflow-hidden" data-id="element-2334">
                        <div className="mt-4 mb-6 md:mb-8" data-id="element-2335">
                          <div className="bg-black/20 p-5 rounded-xl border border-green-500/20 mb-4" data-id="element-2336">
                            <p className="text-xs md:text-lg mb-4 md:mb-6" data-id="element-2337">
                              Možná už teď používáš{' '}
                              <span className="text-green-400" data-id="element-2338">chlorellu</span>{' '}
                              nebo{' '}
                              <span className="text-green-400" data-id="element-2339">ječmen</span> od{' '}
                              <span className="text-green-400" data-id="element-2340">Green Ways</span>
                              . Nebo o nich alespoň{' '}
                              <span className="text-green-400" data-id="element-2341">přemýšlíš</span>.
                              Možná už cítíš, že ti to pomáhá – víc{' '}
                              <span className="text-green-400" data-id="element-2342">energie</span>,
                              lepší{' '}
                              <span className="text-green-400" data-id="element-2343">trávení</span>,
                              silnější{' '}
                              <span className="text-green-400" data-id="element-2344">imunita</span>. A
                              pokud ne ty, tak třeba někdo ve tvé rodině. A
                              právě tady to začíná mít smysl.
                            </p>

                            <p className="text-xs md:text-lg mb-4 md:mb-6" data-id="element-2345">
                              Protože tohle není jen o produktech. Je to o{' '}
                              <span className="text-green-400" data-id="element-2346">
                                skutečné zkušenosti, která má sílu změnit život
                              </span>
                              . A jakmile jednou poznáš, co s tebou tahla změna
                              udělá, začneš přemýšlet nad tím, jak to dostat
                              mezi ostatní lidi, kterým by to mohlo pomoct –
                              tvým kamarádům, rodině, kolegům. A v tu chvíli
                              pochopíš, že přesně o tom je{' '}
                              <span className="text-green-400" data-id="element-2347">
                                podnikání s Green Ways
                              </span>
                              .
                            </p>

                            <p className="text-xs md:text-lg mb-4 md:mb-6" data-id="element-2348">
                              <span className="text-green-400" data-id="element-2349">Green Ways</span>{' '}
                              není žádné obyčejné "sdílej a vyděláš" MLM. Tohle
                              funguje jen tehdy, když{' '}
                              <span className="text-green-400" data-id="element-2350">
                                sám věříš tomu, co děláš
                              </span>{' '}
                              – protože máš vlastní příběh. Víš, co ti{' '}
                              <span className="text-green-400" data-id="element-2351">
                                zelené potraviny
                              </span>{' '}
                              daly. Máš výsledky. A když se bavíš s někým
                              dalším, mluvíš ze srdce, ne z letáku. Nemusíš si
                              nic vymýšlet. Jen sdílíš vlastní zkušenost – a
                              lidi to poznají. Tohle podnikání nestojí na
                              prodeji za každou cenu. Stojí na důvěře, skutečné
                              změně a upřímnosti. A to je důvod, proč funguje.
                            </p>

                            <p className="text-xs md:text-lg mb-4 md:mb-6" data-id="element-2352">
                              A pokud teď přemýšlíš jen nad tím, že jsi našel
                              produkt, na kterém bys mohl vydělat – ale sám jsi
                              ho ještě nevyzkoušel a nemáš s ním žádnou
                              zkušenost, tak tahla část není určená pro tebe.
                              Nejprve si dej šanci zažít, co tyhle potraviny
                              dokážou s tvým tělem i psychikou, a pak uvidíš
                              sám, že tě to přirozeně povede k tomu, pomáhat
                              dalším lidem. Dřív ne.
                            </p>

                            <p className="text-xs md:text-lg mb-0" data-id="element-2353">
                              Je to cesta, jak si{' '}
                              <span className="text-green-400" data-id="element-2354">
                                vytvořit pasivní příjem
                              </span>
                              , ale zároveň{' '}
                              <span className="text-green-400" data-id="element-2355">
                                pomáhat druhým lidem
                              </span>
                              . Nemusíš být nutriční specialista ani fitness
                              trenér. Stačí, že si tím projdeš sám. Že víš, jak
                              se cítíš s a bez zelených potravin. A že máš chuť
                              to poslat dál.
                            </p>

                            <p className="text-xs md:text-lg mb-0" data-id="element-2356">
                              Zároveň se tím otevřou nové možnosti – můžeš se
                              stát součástí
                              <span className="text-green-400" data-id="element-2357">
                                {' '}
                                komunity lidí, kteří žijí zdravě
                              </span>
                              , mají pozitivní přístup k životu a chtějí se
                              posouvat dál. Lidi, co se navzájem podporují,
                              inspirují a sdílí know-how, aby každý mohl růst –
                              zdravotně, osobně i finančně. Tohle prostředí tě
                              může opravdu změnit. Nejen tvé tělo, ale i způsob,
                              jak přemýšlíte. A nejlepší na tom je, že v tom
                              nejsi sám. Budou tady pro tebe lidi, kteří mají
                              roky praxí za sebou a kteří ti jsou schopni předat
                              plán přesně krok po kroku, od úplného začátku s{' '}
                              <span className="text-green-400" data-id="element-2358">
                                podnikáním
                              </span>{' '}
                              až k tomu, že budeš mít pod sebou systém kterému
                              nejen zdravotně pomáháš, ale také na kterých si
                              můžeš tvořit{' '}
                              <span className="text-green-400" data-id="element-2359">
                                pasivní příjem
                              </span>
                              . Pokud tě to zaujme, rád ti ukážu, jak s{' '}
                              <span className="text-green-400" data-id="element-2360">Green Ways</span>{' '}
                              začít podnikat. Probereme spolu možnosti
                              spolupráce, ukážu ti, jak{' '}
                              <span className="text-green-400" data-id="element-2361">
                                zelené potraviny
                              </span>{' '}
                              přirozeně zapojit do každodenního života, jak o
                              nich mluvit, i jak si tím můžeš vybudovat vlastní
                              cestu ke zdravému já, ale i ke svobodě.
                            </p>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  <div className="text-center relative" data-id="element-2362">
                    <div className="absolute inset-0 bg-green-500/10 blur-xl rounded-xl -z-0 animate-pulse" data-id="element-2363"></div>
                    <motion.div whileHover={{
                    scale: 1.05
                  }} transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 10
                  }} className="relative z-10" data-id="element-2364">
                      <Button variant="primary" size="lg" onClick={scrollToContactForm} className="luxury-button px-8 md:px-10 text-sm md:text-base mx-auto" data-id="element-2365">
                        MÁM ZÁJEM O PODNIKÁNÍ S GW
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Vše, co potřebujete vědět o superpotravinách */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="mt-16 max-w-4xl mx-auto" data-id="element-2366">
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 md:p-10 luxury-card border border-green-500/30 shadow-[0_10px_40px_-5px_rgba(34,197,94,0.2)] relative overflow-hidden" data-id="element-2367">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2368"></div>
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2369"></div>
                <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-2370"></div>

                <h4 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center relative" data-id="element-2371">
                  <span className="relative inline-block" data-id="element-2372">
                    <span className="relative z-10" data-id="element-2373">
                      VŠE, CO POTŘEBUJETE VĚDĚT O{' '}
                      <span className="text-green-500" data-id="element-2374">SUPERPOTRAVINÁCH</span>
                    </span>
                    <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-2375"></span>
                  </span>
                </h4>

                <p className="text-center text-gray-300 max-w-2xl mx-auto mb-8" data-id="element-2376">
                  Kompletní průvodce zelenými superpotravinami - vše od účinků a
                  dávkování po detoxikaci, nemoci a kvalitu produktů. Doporučuji
                  si pročíst tuhle sekci před užíváním superpotravin.
                </p>

                <div className="text-center relative" data-id="element-2377">
                  <motion.div whileHover={{
                  scale: 1.05
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 10
                }} className="relative z-10" data-id="element-2378">
                    <Button variant="primary" size="lg" onClick={() => navigate('/superpotraviny-info')} className="luxury-button px-8 py-4 md:px-10 text-base md:text-lg mx-auto" data-id="element-2379">
                      OTEVŘÍT PRŮVODCE SUPERPOTRAVINAMI
                    </Button>
                  </motion.div>
                  <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-xl -z-0 animate-pulse" data-id="element-2380"></div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* FAQ Section */}
          <SuperfoodsFAQSection data-id="element-2381" />

          {/* Back to Home Button - Elegant & Luxurious */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="py-16 md:py-24 flex justify-center items-center" data-id="element-2382">
            <button onClick={() => {
            navigate('/');
            // Scroll to top immediately after navigation
            setTimeout(() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }, 100);
          }} className="group relative px-8 py-4 bg-gray-900/50 hover:bg-gray-900/80 backdrop-blur-sm border border-white/10 hover:border-green-500/50 rounded-full transition-all duration-500 ease-out overflow-hidden shadow-lg hover:shadow-[0_0_25px_rgba(34,197,94,0.15)]" data-id="element-2383">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" data-id="element-2384" />
              <span className="relative flex items-center gap-3 text-gray-400 group-hover:text-white transition-colors duration-300 font-medium tracking-widest text-sm uppercase" data-id="element-2385">
                <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1 text-green-500/70 group-hover:text-green-400" data-id="element-2386" />
                Zpět na hlavní stránku
              </span>
            </button>
          </motion.div>
        </div>

        <div className="section-divider divider-tilt" data-id="element-2387"></div>
      </section>
      <ContactSection data-id="element-2388" />
    </>;
};